/**
 * SPEC-TrainingSimulator §8.3 / §8.3a — pure state helpers for the trainee
 * runner (train-run.tsx, question-rail.tsx). No React, no DOM, so the D20
 * Back semantics, resume position and the "which questions are asked"
 * rule are unit-tested (Phase B audit W1 / W4 / W7).
 *
 *   skippedQuestionIds  — rail questions the KEY does not ask (Q8 when the
 *                         key's Q7 is Yes); known only once `asked` arrives
 *   initialActiveId     — resume position: first question not graded and not skipped
 *   nextActiveId        — after grading: the next askable question, or null after the last
 *   isRevisit           — a graded question before the latest graded one (read-only)
 *   pushHistory / popHistory — D20 Back = un-pick the last change
 *   toggleWhere         — one `where[]` array shared by the chips and the photo checkboxes
 *
 * Phase C adds the display side of the clock and the resolution-note fold:
 *   tickingRemaining    — what the chip shows between server readings (§8.4)
 *   foldResolutionNotes — the real panel's Step 2 + note → one `resolution_notes` string
 *   phaseRuleChip       — the Training-strip rule chip text per phase
 *
 * 2026-09-11 — the reveal outlines the KEY's answer:
 *   keyedAnswerHighlight — the key's `valueHighlight`, or the keyed value / quote
 *                          located in the card text when the key has no anchor
 */

import type { AnswerEntry, ClockMode, ClockReading, HighlightField, QuestionId, SimulatorQuestionConfig, TextHighlight, TraineeSnapshot } from "@shared/simulator-helpers";

/** Ids in `questions` that the server's `asked` list leaves out. Empty until `asked` is known. */
export function skippedQuestionIds(questions: SimulatorQuestionConfig[], asked: QuestionId[] | null): QuestionId[] {
  if (!asked) return [];
  const set = new Set(asked);
  return questions.filter((q) => !set.has(q.id)).map((q) => q.id);
}

export function initialActiveId(
  questions: SimulatorQuestionConfig[],
  graded: Partial<Record<QuestionId, unknown>>,
  skipped: QuestionId[],
): QuestionId | null {
  const skip = new Set(skipped);
  const pending = questions.find((q) => !graded[q.id] && !skip.has(q.id));
  if (pending) return pending.id;
  // Everything graded (or skipped): sit on the last graded question so its feedback shows.
  for (let i = questions.length - 1; i >= 0; i--) if (graded[questions[i].id]) return questions[i].id;
  return questions[0]?.id ?? null;
}

/** The next askable question after `activeId` (ungraded first, else the next askable one), or null when none is left. */
export function nextActiveId(
  questions: SimulatorQuestionConfig[],
  activeId: QuestionId,
  graded: Partial<Record<QuestionId, unknown>>,
  skipped: QuestionId[],
): QuestionId | null {
  const skip = new Set(skipped);
  const idx = questions.findIndex((q) => q.id === activeId);
  const rest = questions.slice(idx + 1).filter((q) => !skip.has(q.id));
  return (rest.find((q) => !graded[q.id]) ?? rest[0])?.id ?? null;
}

/** §8.3a — a graded question that sits before the latest graded one is a read-only revisit. */
export function isRevisit(questions: SimulatorQuestionConfig[], activeId: QuestionId, graded: Partial<Record<QuestionId, unknown>>): boolean {
  if (!graded[activeId]) return false;
  const idx = questions.findIndex((q) => q.id === activeId);
  const latest = questions.reduce((m, q, i) => (graded[q.id] ? i : m), -1);
  return idx < latest;
}

export type DraftHistory = Partial<Record<QuestionId, Array<AnswerEntry | null>>>;

/** Record the draft that is about to be replaced so Back can restore it. */
export function pushHistory(history: DraftHistory, id: QuestionId, previous: AnswerEntry | null): DraftHistory {
  return { ...history, [id]: [...(history[id] ?? []), previous] };
}

/** Back (D20): the draft to restore and the shortened history, or null when there is nothing to undo. */
export function popHistory(history: DraftHistory, id: QuestionId): { draft: AnswerEntry | null; history: DraftHistory } | null {
  const stack = history[id] ?? [];
  if (!stack.length) return null;
  return { draft: stack[stack.length - 1], history: { ...history, [id]: stack.slice(0, -1) } };
}

/** Toggle one evidence source in `where[]`, keeping the evidence-list order (photos first, then notes). */
export function toggleWhere(current: string[] | undefined, target: string, order: string[]): string[] {
  const cur = current ?? [];
  if (cur.includes(target)) return cur.filter((x) => x !== target);
  const next = new Set([...cur, target]);
  return order.filter((v) => next.has(v));
}


// ─── Phase C ─────────────────────────────────────────────────────────────────

/**
 * §8.4 — "the client clock is display only". The server hands back a
 * `ClockReading` computed from `opened_at`; between readings the chip counts
 * down locally from it. Never used to decide anything: /extend and /timeout
 * both recompute server-side, so a wrong local clock costs nothing.
 */
export function tickingRemaining(clock: ClockReading | null | undefined, msSinceReading: number): number | null {
  if (!clock || clock.mode === "none") return null;
  return Math.max(0, clock.remainingSeconds - Math.floor(Math.max(0, msSinceReading) / 1000));
}

/** True once the local countdown says the hard clock is out — the cue to POST /timeout and let the server rule on it. */
export function shouldPostTimeout(clock: ClockReading | null | undefined, msSinceReading: number): boolean {
  if (!clock || clock.mode !== "hard") return false;
  return (tickingRemaining(clock, msSinceReading) ?? 1) <= 0;
}

/** "+ Time" is offered for the whole item on an extendable clock (Sarah, 2026-09-15); it used to wait for the warning window. */
export function canExtendNow(clock: ClockReading | null | undefined, _msSinceReading: number, _warningSeconds: number): boolean {
  return !!clock && clock.mode === "extendable";
}

/**
 * The real `AgentResolutionPanel` hands back a Step 2 initial action and an
 * initial note; `simulator_answers` has one `resolution_notes` column and no
 * migration was wanted for the other two (Sarah, 2026-09-09). Fold them into
 * one line so the Senior drill-down keeps the whole answer.
 */
export function foldResolutionNotes(input: {
  initialAction?: string | null;
  initialActionOther?: string | null;
  initialNote?: string | null;
}): string | null {
  const ACTION: Record<string, string> = { phone: "Calling", text: "Texting", other: "Other" };
  const action = input.initialAction ? ACTION[input.initialAction] ?? input.initialAction : null;
  const other = (input.initialActionOther ?? "").trim();
  const note = (input.initialNote ?? "").trim();
  const head = action ? `Initial action: ${action}${action === "Other" && other ? ` (${other})` : ""}` : null;
  if (head && note) return `${head} — ${note}`;
  return head ?? (note || null);
}

/**
 * The Training strip's rule chip (§8.2): what this phase does about feedback and
 * the clock. The clock half is worded from the clock's ACTUAL mode, not the
 * phase number — with `simulator.phase2Enforced` on, Phase 2 runs an extendable
 * clock, and the chip used to promise a soft one beside a red pulsing timer and
 * a "+ Time" button (AUDIT-PhaseC L2).
 */
export function phaseRuleChip(phase: number, targetSeconds: number, mode?: ClockMode): string {
  const t = formatClock(targetSeconds);
  const clockPart = (mode ?? (phase <= 1 ? "none" : phase === 2 ? "soft" : phase === 3 ? "extendable" : "hard")) satisfies ClockMode;
  switch (clockPart) {
    case "none": return "No clock · instant feedback";
    case "soft": return `Soft clock · target ${t} · feedback at the end`;
    case "extendable": return phase === 2
      ? `Clock ${t} · + Time any time · feedback at the end`
      : `Clock ${t} · + Time any time`;
    case "hard": return `Hard clock ${t} · no extensions`;
  }
}

export function formatClock(seconds: number | null | undefined): string {
  const whole = Math.max(0, Math.floor(seconds ?? 0));
  return `${Math.floor(whole / 60)}:${(whole % 60).toString().padStart(2, "0")}`;
}

// ─── Reveal: the keyed answer, outlined (2026-09-11) ─────────────────────────

/** Card fields searched for a q1/q2 keyed value, most likely first. */
const VALUE_FIELDS: readonly HighlightField[] = ["address", "destinationNotes", "taskNotes", "recipientName", "company"];

function snapshotField(snapshot: TraineeSnapshot, field: HighlightField): string | null {
  switch (field) {
    case "company": return snapshot.customer;
    case "recipientName": return snapshot.recipientName;
    case "address": return snapshot.address;
    case "taskNotes": return snapshot.taskNotes;
    case "destinationNotes": return snapshot.destinationNotes;
    case "completionNote": return snapshot.driverCompletionNotes;
  }
}

/**
 * The anchor to outline as THE answer once a q1/q2/q7 question is graded
 * (Sarah, 2026-09-11: what the trainee sees highlighted after "Check answer"
 * must be the key's solution). Prefers the key's own `valueHighlight`; a key
 * saved before highlight capture existed carries only the value, so the
 * value (q1/q2) or quote (q7) is located in the card text instead — first
 * occurrence, most likely field first. Null when there is nothing to outline
 * (N/A, blank, other questions, or text that is not on the cards).
 */
export function keyedAnswerHighlight(id: QuestionId, keyed: AnswerEntry | null | undefined, valueHighlight: TextHighlight | null | undefined, snapshot: TraineeSnapshot): TextHighlight | null {
  if (valueHighlight && typeof valueHighlight.text === "string" && valueHighlight.text.trim()) return valueHighlight;
  const e = (keyed ?? {}) as any;
  let text: string | null = null;
  let fields: readonly HighlightField[] = [];
  if (id === "q1_street" || id === "q2_unit") { text = typeof e.value === "string" ? e.value : null; fields = VALUE_FIELDS; }
  else if (id === "q7_return_note") { text = e.value === "yes" && typeof e.quote === "string" ? e.quote : null; fields = ["completionNote"]; }
  const needle = text?.trim() ?? "";
  if (!needle || needle.toUpperCase() === "N/A") return null;
  for (const field of fields) {
    const hay = snapshotField(snapshot, field);
    if (!hay) continue;
    const occurrence = wholeTokenOccurrence(hay, needle);
    if (occurrence) return { field, text: needle, occurrence };
  }
  return null;
}

const isWordChar = (c: string | undefined) => !!c && /[A-Za-z0-9\u00C0-\u024F]/.test(c);

/**
 * The 1-based occurrence of `needle` in `hay` that stands as a whole token
 * (no letter or digit touching either side), or null. Counted the way the
 * highlight surface's `rangeFor` counts — every `indexOf` hit, overlapping
 * included — so a legacy unit "42" in "42nd St, Unit 42" outlines the unit,
 * not the street (audit W3). Falls back to the first plain hit when no
 * whole-token one exists ("703" inside "703A").
 */
export function wholeTokenOccurrence(hay: string, needle: string): number | null {
  let n = 0;
  let i = hay.indexOf(needle);
  while (i !== -1) {
    n++;
    if (!isWordChar(hay[i - 1]) && !isWordChar(hay[i + needle.length])) return n;
    i = hay.indexOf(needle, i + 1);
  }
  return n ? 1 : null;
}
